import { WarningFilled } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { taxCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import {
  defaultASCCalculationMethodOptions,
  defaultCisStatus,
  defaultPartnerCategoryOptions,
  defaultPaymentFrequency,
  defaultPRSIEeClassesOptions,
  defaultPRSIExemptionOptions,
  defaultsBusinessTypeOptions,
  defaultTaxBasisIrelandOptions,
  defaultTaxBasisOptions,
  defaultUSCExemptionOptions,
  defaultUSCTiersOptions,
  directorshipOptions,
  employmentStatusOptions,
  freeportNIcategories,
  NICategoryOptions,
  paymentModelOptions,
  getIrelandPaymentModelOptions,
  rctStatusOptions,
  starterDeclarationOptions,
  starterTypeOptions,
  USCRateList,
} from '@nxsys/constants/AppConstants';
import {
  BusinessTypeEnum,
  ContractDetailTypeEnum,
  EmploymentStatusEnum,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import {
  calcUSCRateAmount,
  calcUSCRateFrequency,
  getAppConfig,
  showCurrencyByCountry,
  isValidNINumber,
  isValidPPSNumber,
  isNIDirectorMidYear,
} from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import clsx from 'clsx';
import { toNumber } from 'lodash';
import { Moment } from 'moment';
import React, { Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';
import WorkingPatternFields from '../../components/WorkingPatternFields';
import {
  findDefaultWorkingPattern,
  WorkingPatternValues,
} from '../../components/workingPatternUtils';
import {
  allowsNoDepartment,
  departmentOptionsFor,
} from '@nxsys/utils/department-scope';

export type EmploymentValues = {
  contractDetailType: Option<string> | null;
  employmentStartDate: Moment | null;
  employmentBasis: Option<string> | null;
  paymentMethod: Option<string> | null;
  employer: Option<string> | null;
  department: Option<number> | null;
  businessType: Option<string> | null;
  utrNumber: string;
  cisStatus: Option<string> | null;
  companyName: string;
  registrationNumber: string;
  vatNumber: string;
  domesticReverseChargeApplicable: boolean;
  firmName: string;
  partnersName: string;
  partnersCategory: Option<string> | null;
  personalVerificationNumber: string;
  verificationDate: Moment | null;
  matchDate: Moment | null;
  vatRegNo: string;
  NINumber: string;
  NICategory: Option<string> | null;
  workplacePostcode: string;
  NIDirectorStartDate: Moment | null;
  NIDirectorMethod: Option<string> | null;
  payFrequency: string;
  salary: string;
  starterDeclaration: Option<string> | null;
  starterType: Option<string> | null;
  previousPayrollId: string;
  previousPayrollIdUnknown: boolean;
  p45GrossToDate: string;
  p45TaxToDate: string;
  employeeNI: string;
  employerNI: string;
  employeePension: string;
  employerPension: string;
  studentLoan: string;
  postGraduateStudentLoan: string;
  smp: string;
  spp: string;
  sap: string;
  shpp: string;
  ssp: string;
  sncp: string;
  grossToLel: string;
  grossLelToPt: string;
  grossPtToUEL: string;
  gross: string;
  grossForTax: string;
  grossForNI: string;
  tax: string;
  // Director NI Contribution fields
  grossToLelAsDirector: string;
  grossLelToPtAsDirector: string;
  grossPtToUELAsDirector: string;
  grossForNIAsDirector: string;
  eeNIAsDirector: string;
  erNIAsDirector: string;
  taxCode: string;
  taxBasis: Option<string> | null;
  subcontractorRefNumber: string;
  ppsNumber: string;
  employmentStatusIreland: Option<string> | null;
  employmentId: string;
  prsiEeClasses: Option<string> | null;
  prsiExemption: Option<string> | null;
  directorShip: Option<string> | null;
  taxCreditFrequency: string;
  taxCreditAnnual: string;
  taxRateCutOffPoints: string;
  taxRateCutOffPointsFrequency: string;
  uscExemption: Option<string> | null;
  cutOffPointsType: Option<string> | null;
  cutOffPointsZeroPointFive: string;
  cutOffPointsZeroPointFiveFrequency: string;
  cutOffPointsTwo: string;
  cutOffPointsTwoFrequency: string;
  cutOffPointsFourPointFive: string;
  cutOffPointsFourPointFiveFrequency: string;
  cutOffPointsEight: string;
  cutOffPointsEightFrequency: string;
  totalLPTToBeDeducted: string;
  ascCalculationMethod: Option<string> | null;
} & WorkingPatternValues;

export type EmploymentFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => EmploymentValues;
  resetFields: () => void;
};

type EmploymentFormProps = {
  formRef?: Ref<EmploymentFormRef>;
  initialValues?: EmploymentValues;
  contractDetailType: string;
  employmentStatus?: string;
};

const EmploymentForm: React.FC<EmploymentFormProps> = ({
  formRef,
  initialValues,
  contractDetailType,
  employmentStatus,
}) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const { payrollCountry, isBureau, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  // Country-only: an Irish tenant gets Irish fields regardless of product
  // (bureau or umbrella). `isUK` below stays bureau-scoped — it drives
  // bureau-specific defaulting (paymentMethod hidden/forced to PAYEE), which
  // umbrella still needs its per-employee payment-method picker for.
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // Used only where the old gate was the OR `!isBureau || isUK` (any umbrella
  // tenant, or bureau-UK) — algebraically that OR equals `!isBureau ||
  // isUKCountry`... equals true for every cell except bureau-Ireland, which
  // is also what "any umbrella tenant" alone was standing in for by
  // accident. Naming it by country makes the umbrella-Ireland fix explicit:
  // UK-umbrella/UK-bureau stay true, Ireland-bureau stays false,
  // Ireland-umbrella flips from true (bug — UK fields leaking in) to false.
  const isUKCountry = payrollCountry === UNITED_KINGDOM;
  const defaultPaymentModel = isIreland
    ? getIrelandPaymentModelOptions(isBureau)
    : getAppConfig('paymentModels');

  const [form] = Form.useForm<EmploymentValues>();
  const watchValues = Form.useWatch([], form);

  // NX-6426: see SettingsGeneral — the backend discards these balances unless
  // the director start date falls part-way through the current tax year.
  const isNIMiddleJoined = isNIDirectorMidYear(
    employmentStatus === EmploymentStatusEnum.DIRECTOR,
    watchValues?.NIDirectorStartDate,
  );

  const isExistingEmployee =
    contractDetailType === ContractDetailTypeEnum.EXISTING_EMPLOYEE;

  // Opening Balances are mandatory for existing employees (0.00 accepted).
  const openingBalanceRules = isExistingEmployee
    ? [{ required: true, message: messages['form.pleaseEnter'] }]
    : [];

  const [showOpeningBalance, setShowOpeningBalance] = useState(false);

  // Auto-set starterType for existing employees
  useEffect(() => {
    if (isExistingEmployee) {
      form.setFieldsValue({
        starterType: starterTypeOptions.find((o) => o.value === 'EXISTING') || null,
      });
    }
  }, [contractDetailType]);

  // Existing employees must always see the Opening Balances section expanded.
  useEffect(() => {
    if (isExistingEmployee) {
      setShowOpeningBalance(true);
    }
  }, [contractDetailType]);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error: any) {
        const firstField = error?.errorFields?.[0]?.name;
        if (firstField) {
          form.scrollToField(firstField, {
            behavior: 'smooth',
            block: 'center',
          });
        }
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const employerCompanyId: string | undefined = watchValues?.employer?.value;
  const departmentOptions = useMemo(() => {
    const options = departmentOptionsFor(
      employerCompanyId
        ? { id: employerCompanyId, departments: watchValues?.employer?.fullData?.departments }
        : null,
      user?.departmentScope,
    );
    return options.map((d) => ({ label: d.departmentName, value: d.id }));
  }, [employerCompanyId, watchValues?.employer?.fullData, user?.departmentScope]);
  const canClearDepartment = allowsNoDepartment(
    employerCompanyId,
    user?.departmentScope,
  );

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={(changedValues: any) => {
        // Auto-fill taxCode and taxBasis based on starter declaration selection.
        // UK-only concept (tax code + W1M1/CUMULATIVE basis) — Ireland has no
        // starter declaration/tax code, so this must not fire for an Irish
        // employee record (bureau or umbrella).
        if (
          !isIreland &&
          'starterDeclaration' in changedValues &&
          changedValues.starterDeclaration?.value
        ) {
          switch (changedValues.starterDeclaration.value) {
            case 'FIRST_JOB_SINCE_START_TAX_YEAR':
              form.setFieldsValue({
                taxCode: '1257L',
                taxBasis: defaultTaxBasisOptions.find((o: any) => o.value === 'CUMULATIVE'),
              });
              break;
            case 'CURRENTLY_ONLY_JOB':
              form.setFieldsValue({
                taxCode: '1257L',
                taxBasis: defaultTaxBasisOptions.find((o: any) => o.value === 'W1M1'),
              });
              break;
            case 'HAVE_ANOTHER_JOB':
              form.setFieldsValue({
                taxCode: 'BR',
                taxBasis: defaultTaxBasisOptions.find((o: any) => o.value === 'CUMULATIVE'),
              });
              break;
          }
        }
      }}
      initialValues={{
        ...initialValues,
        ...(isIreland ? { cisStatus: rctStatusOptions[1] } : {}),
        ...(isUK
          ? {
              paymentMethod: paymentModelOptions.find(
                (pm) => pm.value === PAYMENT_MODEL_ENUM.PAYEE,
              ),
            }
          : {}),
        NIDirectorMethod: initialValues?.NIDirectorMethod ?? {
          label: 'Alternative',
          value: 'ALTERNATIVE',
        },
      }}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold mb-[40px]">
        {messages['common.employment']}
      </div>
      <Row gutter={24}>
        <Col span={24} className={clsx({ hidden: isUK })}>
          <Form.Item
            name="paymentMethod"
            label={messages['common.paymentMethod']}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelect
              options={defaultPaymentModel as any}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="employer"
            label={employerLabels.label}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelectSync
              apiType="searchCompany"
              type="company"
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
              onChangeValue={(value: any) => {
                form.setFieldsValue({ employer: value, department: null });

                // Auto-fill company defaults (only when field is empty)
                const company = value?.fullData;
                if (company) {
                  const current = form.getFieldsValue(true);
                  const updates: Record<string, any> = {};

                  if (!current.taxCode && company.taxCode) {
                    updates.taxCode = company.taxCode;
                  }
                  if (!current.payFrequency && company.paymentFrequency) {
                    updates.payFrequency = company.paymentFrequency;
                  }
                  if (!current.NICategory && company.NICategory) {
                    const niOpt = NICategoryOptions.find(
                      (o: any) => o.value === company.NICategory,
                    );
                    if (niOpt) updates.NICategory = niOpt;
                  }
                  if (!current.taxBasis && company.taxBasis) {
                    const tbOpt = defaultTaxBasisOptions.find(
                      (o: any) => o.value === company.taxBasis,
                    );
                    if (tbOpt) updates.taxBasis = tbOpt;
                  }

                  if (Object.keys(updates).length) {
                    form.setFieldsValue(updates);
                  }
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="department"
            label={messages['common.department']}
            rules={[
              {
                required: !canClearDepartment,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <AppSelect
              options={departmentOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
              allowClear={canClearDepartment}
            />
          </Form.Item>
        </Col>
        {watchValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.CIS ||
        watchValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.NON_CIS ? (
          <>
            {/* NX-5228: country first. `!isBureau` alone won for umbrella
                Ireland, so a CIS/NON_CIS worker there was asked for UK business
                type, UTR and VAT and had no field for RCT status or the Revenue
                subcontractor reference — the isIreland arm below was
                unreachable on umbrella. */}
            {!isBureau && !isIreland ? (
              <>
                <Col span={24}>
                  <Form.Item
                    name="businessType"
                    label={messages['common.businessType']}
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={defaultsBusinessTypeOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                {watchValues?.businessType?.value ===
                  BusinessTypeEnum.SOLE_TRADER && (
                  <>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="utrNumber"
                        label={messages['common.UTRNumber']}
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          maxLength={13}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="cisStatus"
                        label={messages['common.cisStatus']}
                      >
                        <AppSelect
                          options={defaultCisStatus}
                          placeholder={messages['form.select']}
                          className="w-full !min-w-0"
                        />
                      </Form.Item>
                    </Col>
                    {watchValues?.paymentMethod?.value ===
                      PAYMENT_MODEL_ENUM.CIS && (
                      <>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="personalVerificationNumber"
                            label={
                              messages['common.personalVerificationNumber']
                            }
                          >
                            <AppInput placeholder={messages['form.enter']} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="verificationDate"
                            label={messages['common.verificationDate']}
                          >
                            <AppDatePicker
                              placeholder={messages['form.selectDate']}
                              format="DD/MM/YYYY"
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="matchDate"
                            label={messages['common.matchDate']}
                          >
                            <AppDatePicker
                              placeholder={messages['form.selectDate']}
                              format="DD/MM/YYYY"
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="vatRegNo"
                            label={messages['common.vatRegNo']}
                          >
                            <AppInput
                              placeholder={messages['form.enter']}
                              maxLength={9}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="domesticReverseChargeApplicable"
                            label={
                              messages['common.domesticReverseChargeApplicable']
                            }
                            valuePropName="checked"
                            layout="horizontal"
                            labelCol={{ span: 18 }}
                            wrapperCol={{ span: 6 }}
                          >
                            <div className="flex justify-end items-end">
                              <AppSwitch />
                            </div>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
                {(watchValues?.businessType?.value ===
                  BusinessTypeEnum.COMPANY ||
                  watchValues?.businessType?.value ===
                    BusinessTypeEnum.PARTNERSHIP) && (
                  <>
                    {watchValues?.businessType?.value ===
                      BusinessTypeEnum.COMPANY && (
                      <>
                        <Col span={24}>
                          <Form.Item
                            name="companyName"
                            label={
                              isBureau
                                ? messages['common.bureau.companyName']
                                : messages['common.companyName']
                            }
                            rules={[
                              {
                                required: true,
                                message: `${messages['form.pleaseEnter']} ${isBureau ? messages['common.bureau.companyName'] : messages['common.companyName']}`,
                              },
                            ]}
                          >
                            <AppInput placeholder={messages['form.enter']} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="registrationNumber"
                            label={messages['common.registrationNumber']}
                            rules={[
                              {
                                required: true,
                                message: `${messages['form.pleaseEnter']} ${messages['common.registrationNumber']}`,
                              },
                            ]}
                          >
                            <AppInput placeholder={messages['form.enter']} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {watchValues?.businessType?.value ===
                      BusinessTypeEnum.PARTNERSHIP && (
                      <>
                        <Col span={24}>
                          <Form.Item
                            name="firmName"
                            label={messages['common.firmName']}
                            rules={[
                              {
                                required: true,
                                message: `${messages['form.pleaseEnter']} ${messages['common.firmName']}`,
                              },
                            ]}
                          >
                            <AppInput placeholder={messages['form.enter']} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="partnersName"
                            label={messages['common.partnersName']}
                            rules={[
                              {
                                required: true,
                                message: `${messages['form.pleaseEnter']} ${messages['common.partnersName']}`,
                              },
                            ]}
                          >
                            <AppInput placeholder={messages['form.enter']} />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="partnersCategory"
                            label={messages['common.partnersCategory']}
                          >
                            <AppSelect
                              options={defaultPartnerCategoryOptions}
                              placeholder={messages['form.select']}
                              className="w-full !min-w-0"
                            />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    <Col span={24}>
                      <Form.Item
                        name="vatNumber"
                        label={messages['common.vatNumber']}
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          maxLength={9}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="domesticReverseChargeApplicable"
                        label={
                          messages['common.domesticReverseChargeApplicable']
                        }
                        valuePropName="checked"
                        layout="horizontal"
                        labelCol={{ span: 18 }}
                        wrapperCol={{ span: 6 }}
                      >
                        <div className="flex justify-end items-end">
                          <AppSwitch />
                        </div>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </>
            ) : isIreland ? (
              <>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="cisStatus"
                    label={messages['common.rctStatus']}
                  >
                    <AppSelect
                      options={rctStatusOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="subcontractorRefNumber"
                    label={messages['common.subcontractorReferenceNumber']}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col span={24}>
              <Form.Item
                name="payFrequency"
                label={messages['common.payFrequency']}
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppPayFrequencySelect
                  placeholder={messages['form.select']}
                  className="w-full !min-w-0"
                />
              </Form.Item>
            </Col>
          </>
        ) : watchValues?.paymentMethod ? (
          <>
            {isUKCountry ? (
              <>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="NINumber"
                    label={messages['common.NINumber']}
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!value) return;
                          if (!isValidNINumber(value)) {
                            throw new Error(
                              messages['form.invalidNINumber'] ??
                                'Invalid NI number',
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      upperCaseAll
                      maxLength={9}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="NICategory"
                    label={messages['common.NICategory']}
                  >
                    <AppSelect
                      options={NICategoryOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                {freeportNIcategories.some(
                  (f) => f.value === watchValues?.NICategory?.value,
                ) && (
                  <Col sm={24} md={12}>
                    <Form.Item
                      name="workplacePostcode"
                      label={messages['common.workplacePostcode'] ?? 'Workplace Postcode'}
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput
                        placeholder={messages['form.enter']}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col sm={24} md={12}>
                  <Form.Item
                    name="payFrequency"
                    label={messages['common.payFrequency']}
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppPayFrequencySelect
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item name="salary" label={messages['common.salary']}>
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="taxCode"
                    label={messages['common.taxCode']}
                    rules={[taxCodeValidationRule(messages)]}
                  >
                    <AppInput upperCaseAll placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="taxBasis"
                    label={messages['common.taxBasis']}
                  >
                    <AppSelect
                      options={defaultTaxBasisOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="starterDeclaration"
                    label={messages['common.starterDeclaration']}
                  >
                    <AppSelect
                      options={starterDeclarationOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="starterType"
                    label={messages['common.starterType'] ?? 'Starter Type'}
                  >
                    <AppSelect
                      options={starterTypeOptions}
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                      disabled={
                        contractDetailType ===
                        ContractDetailTypeEnum.EXISTING_EMPLOYEE
                      }
                    />
                  </Form.Item>
                </Col>
                {employmentStatus === EmploymentStatusEnum.DIRECTOR &&
                  !isIreland && (
                    <>
                      <Col sm={24} md={12}>
                        <Form.Item
                          name="NIDirectorStartDate"
                          label={
                            messages['common.directorStartDate'] ??
                            'Director Start Date'
                          }
                        >
                          <AppDatePicker
                            placeholder={messages['form.selectDate']}
                            format="DD/MM/YYYY"
                            className="w-full"
                          />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          name="NIDirectorMethod"
                          label={
                            messages['common.niCalculationMethod'] ??
                            'NI Calculation Method'
                          }
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseSelect'],
                            },
                          ]}
                        >
                          <AppSelect
                            options={[
                              { label: 'Annual', value: 'ANNUAL' },
                              { label: 'Alternative', value: 'ALTERNATIVE' },
                            ]}
                            placeholder={messages['form.select']}
                            className="w-full !min-w-0"
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                {contractDetailType ===
                  ContractDetailTypeEnum.EXISTING_EMPLOYEE && (
                  <>
                    <Col span={24}>
                      <Form.Item
                        name="previousPayrollId"
                        label={messages['common.previousPayrollId']}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (
                                !value &&
                                !form.getFieldValue('previousPayrollIdUnknown')
                              ) {
                                return Promise.reject(
                                  new Error(
                                    'Previous Payroll ID is required, or tick Previous Payroll ID Unknown',
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        required={!watchValues?.previousPayrollIdUnknown}
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          disabled={
                            watchValues?.previousPayrollIdUnknown === true
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div className="flex items-center justify-between mb-6">
                        <span>
                          {messages['common.previousPayrollIdUnknown'] ??
                            'Previous Payroll ID Unknown'}
                        </span>
                        <Form.Item
                          name="previousPayrollIdUnknown"
                          valuePropName="checked"
                          className="!mb-0"
                        >
                          <AppSwitch
                            onChange={(checked: boolean) => {
                              if (checked) {
                                form.setFieldValue('previousPayrollId', '');
                              }
                              form.validateFields(['previousPayrollId']);
                            }}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </>
                )}
                <Col span={24}>
                  <div className="h-[8px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                  <div className="text-[16px] mb-[4px]">
                    {messages['common.previousJobTaxInformationP45P60']}
                  </div>
                  <div className="text-[14px] text-[#71717A] mb-[24px]">
                    {
                      messages[
                        'common.previousJobTaxInformationP45P60HelperText'
                      ]
                    }
                  </div>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="p45GrossToDate"
                    label={messages['common.p45GrossToDate']}
                  >
                    <div className="text-[12px] text-[#71717A] mb-2">
                      {messages['common.fromPreviousEmployer']}
                    </div>
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="p45TaxToDate"
                    label={messages['common.p45TaxToDate']}
                  >
                    <div className="text-[12px] text-[#71717A] mb-2">
                      {messages['common.fromPreviousEmployer']}
                    </div>
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                {!isExistingEmployee && (
                  <Col span={24}>
                    <AppButton
                      variant="outlined"
                      leftIcon={
                        <Icons.IconFilePlus
                          color={defaultTheme.theme.palette.brand}
                        />
                      }
                      name={messages['common.openingBalance']}
                      className="mb-[24px]"
                      disabled
                      onClick={() => setShowOpeningBalance(!showOpeningBalance)}
                    />
                  </Col>
                )}
                {(isExistingEmployee || showOpeningBalance) && (
                  <>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    {isExistingEmployee && (
                      <Col span={24}>
                        <div
                          className="mb-[24px] flex gap-x-3 rounded-lg px-4 py-3"
                          style={{
                            background: '#FFFBE6',
                            border: '1px solid #FFE58F',
                          }}
                        >
                          <WarningFilled
                            style={{
                              fontSize: 20,
                              color: '#FAAD14',
                              marginTop: 2,
                            }}
                          />
                          <div>
                            <div className="text-[#1A1D1F]">
                              {messages['common.openingBalanceInfo']}
                            </div>
                            <div className="mt-[4px] text-[13px] text-[#71717A]">
                              {messages['common.openingBalanceInfoNote']}
                            </div>
                          </div>
                        </div>
                      </Col>
                    )}
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.yearToDateFigures']}
                      </div>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="employeeNI"
                        label={messages['common.employeeNI']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="employerNI"
                        label={messages['common.employerNI']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="employeePension"
                        label={messages['common.employeePension']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="employerPension"
                        label={messages['common.employerPension']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="studentLoan"
                        label={messages['common.studentLoan']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="postGraduateStudentLoan"
                        label={messages['common.postGraduateStudentLoan']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.statutoryYearToDate']}
                      </div>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="smp" label={messages['common.smp']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="spp" label={messages['common.spp']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="sap" label={messages['common.sap']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="shpp" label={messages['common.shpp']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="ssp" label={messages['common.ssp']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="sncp" label={messages['common.sncp']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.calculationBreakdownYearToDate']}
                      </div>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="grossToLel"
                        label={messages['common.grossToLel']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="grossLelToPt"
                        label={messages['common.grossLelToPt']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="grossPtToUEL"
                        label={messages['common.grossPtToUEL']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="gross" label={messages['common.gross']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="grossForTax"
                        label={messages['common.grossForTax']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        name="grossForNI"
                        label={messages['common.grossForNI']}
                        rules={openingBalanceRules}
                      >
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item name="tax" label={messages['common.tax']} rules={openingBalanceRules}>
                        <AppCurrencyInput
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>

                    {/* Director NI Contribution (only when employee is a Director) */}
                    {isNIMiddleJoined && (
                      <>
                        <Col span={24}>
                          <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                        </Col>
                        <Col span={24}>
                          <div className="text-[16px] mb-[16px]">
                            {messages['common.niContributionAsDirector'] ?? 'NI Contribution as Director'}
                          </div>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="grossToLelAsDirector"
                            label={`${messages['common.grossToLel'] ?? 'Gross to LEL'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="grossLelToPtAsDirector"
                            label={`${messages['common.grossLelToPt'] ?? 'Gross LEL to PT'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="grossPtToUELAsDirector"
                            label={`${messages['common.grossPtToUEL'] ?? 'Gross PT to UEL'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="grossForNIAsDirector"
                            label={`${messages['common.grossForNI'] ?? 'NIable Gross'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="eeNIAsDirector"
                            label={`${messages['common.employeeNI'] ?? 'Employee NI'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            name="erNIAsDirector"
                            label={`${messages['common.employerNI'] ?? 'Employer NI'} (as director)`}
                            rules={openingBalanceRules}
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
              </>
            ) : isIreland ? (
              <>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.taxBasis']}
                    name="taxBasis"
                  >
                    <AppSelect
                      options={defaultTaxBasisIrelandOptions ?? []}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item name="salary" label={messages['common.salary']}>
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={messages['common.employmentStatus']}
                    name="employmentStatusIreland"
                  >
                    <AppSelect
                      options={employmentStatusOptions ?? []}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="ppsNumber"
                    label={messages['common.ppsNumber']}
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!value) return;
                          if (!isValidPPSNumber(value)) {
                            throw new Error(
                              messages['form.invalidPPSNumber'] ??
                                'PPS Number format must be 7 digits followed by 1 or 2 letters',
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                      upperCaseAll
                      maxLength={9}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    name="employmentId"
                    label={messages['common.employmentId']}
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={messages['common.class']}
                    name="prsiEeClasses"
                  >
                    <AppSelect
                      options={defaultPRSIEeClassesOptions}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                      optionRender={(option: any) => (
                        <div>
                          <div>{option.label}</div>
                          <small className="text-muted">
                            {option.data?.desc}
                          </small>
                        </div>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={messages['common.exemption']}
                    name="prsiExemption"
                  >
                    <AppSelect
                      options={defaultPRSIExemptionOptions ?? []}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.directorship']}
                    name="directorShip"
                  >
                    <AppSelect
                      options={directorshipOptions ?? []}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.totalLPTToBeDeducted']}
                    name="totalLPTToBeDeducted"
                  >
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      className="w-full !min-w-0"
                      placeholder={messages['form.enter']}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={messages['common.ascCalculationMethod']}
                    name="ascCalculationMethod"
                  >
                    <AppSelect
                      options={defaultASCCalculationMethodOptions ?? []}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="payFrequency"
                    label={messages['common.payFrequency']}
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppPayFrequencySelect
                      placeholder={messages['form.select']}
                      className="w-full !min-w-0"
                    />
                  </Form.Item>
                </Col>
                {watchValues?.payFrequency && (
                  <>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.taxCredit']}
                      </div>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label={
                          defaultPaymentFrequency.find(
                            (item) => item.value === watchValues?.payFrequency,
                          )?.label || 'Weekly'
                        }
                        name="taxCreditFrequency"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          className="w-full !min-w-0"
                          placeholder={messages['form.enter']}
                          onChange={(val) => {
                            form.setFieldValue(
                              'taxCreditFrequency' as any,
                              val,
                            );
                            form.setFieldValue(
                              'taxCreditAnnual',
                              calcUSCRateAmount(
                                watchValues?.payFrequency,
                                toNumber(val),
                              ),
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label={messages['common.annual']}
                        name="taxCreditAnnual"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          className="w-full !min-w-0"
                          placeholder={messages['form.enter']}
                          onChange={(val) => {
                            form.setFieldValue(
                              'taxCreditAnnual',
                              val,
                            );
                            form.setFieldValue(
                              'taxCreditFrequency' as any,
                              calcUSCRateFrequency(
                                watchValues?.payFrequency,
                                toNumber(val),
                              ),
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.cutOffPoint']}
                      </div>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label={
                          defaultPaymentFrequency.find(
                            (item) => item.value === watchValues?.payFrequency,
                          )?.label || 'Weekly'
                        }
                        name="taxRateCutOffPointsFrequency"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          className="w-full !min-w-0"
                          placeholder={messages['form.enter']}
                          onChange={(val) => {
                            form.setFieldValue(
                              'taxRateCutOffPointsFrequency' as any,
                              val,
                            );
                            form.setFieldValue(
                              'taxRateCutOffPoints',
                              calcUSCRateAmount(
                                watchValues?.payFrequency,
                                toNumber(val),
                              ),
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item
                        label={messages['common.annual']}
                        name="taxRateCutOffPoints"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          className="w-full !min-w-0"
                          placeholder={messages['form.enter']}
                          onChange={(val) => {
                            form.setFieldValue(
                              'taxRateCutOffPoints',
                              val,
                            );
                            form.setFieldValue(
                              'taxRateCutOffPointsFrequency' as any,
                              calcUSCRateFrequency(
                                watchValues?.payFrequency,
                                toNumber(val),
                              ),
                            );
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-[24px]" />
                    </Col>
                    <Col span={24}>
                      <div className="text-[16px] mb-[16px]">
                        {messages['common.usc']}
                      </div>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={messages['common.exemption']}
                        name="uscExemption"
                      >
                        <AppSelect
                          options={defaultUSCExemptionOptions ?? []}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={messages['common.uscCutOffPoints']}
                        name="cutOffPointsType"
                      >
                        <AppSelect
                          options={defaultUSCTiersOptions ?? []}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    {USCRateList[
                      watchValues?.cutOffPointsType
                        ?.value as keyof typeof USCRateList
                    ]?.map((rate, index) => {
                      const isHighestPoint =
                        index ===
                        USCRateList[
                          watchValues?.cutOffPointsType
                            ?.value as keyof typeof USCRateList
                        ].length -
                          1;

                      return (
                        <Col span={24} key={rate.key}>
                          <div className="text-[16px] mb-[24px]">
                            {rate.label}
                          </div>
                          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-[16px]">
                            <Form.Item
                              label={
                                defaultPaymentFrequency.find(
                                  (item) =>
                                    item.value === watchValues?.payFrequency,
                                )?.label || 'Weekly'
                              }
                              name={`${rate.key}Frequency`}
                            >
                              <AppCurrencyInput
                                prefix={showCurrencyByCountry(payrollCountry)}
                                className="w-full !min-w-0"
                                placeholder={messages['form.enter']}
                                onChange={(val) => {
                                  form.setFieldValue(
                                    `${rate.key}Frequency` as any,
                                    val,
                                  );
                                  form.setFieldValue(
                                    rate.key as any,
                                    calcUSCRateAmount(
                                      watchValues?.payFrequency,
                                      toNumber(val),
                                    ),
                                  );
                                }}
                                {...(isHighestPoint && {
                                  placeholder: messages['common.balance'],
                                  disabled: true,
                                })}
                              />
                            </Form.Item>
                            <Form.Item
                              label={messages['common.annual']}
                              name={rate.key}
                            >
                              <AppCurrencyInput
                                prefix={showCurrencyByCountry(payrollCountry)}
                                className="w-full !min-w-0"
                                placeholder={messages['form.enter']}
                                onChange={(val) => {
                                  form.setFieldValue(
                                    rate.key as any,
                                    val,
                                  );
                                  form.setFieldValue(
                                    `${rate.key}Frequency` as any,
                                    calcUSCRateFrequency(
                                      watchValues?.payFrequency,
                                      toNumber(val),
                                    ),
                                  );
                                }}
                                {...(isHighestPoint && {
                                  placeholder: messages['common.balance'],
                                  disabled: true,
                                })}
                              />
                            </Form.Item>
                          </div>
                        </Col>
                      );
                    })}
                  </>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </Row>
      {/* NX-4948: Working Pattern (Old UI parity) */}
      <WorkingPatternFields
        form={form}
        companyId={watchValues?.employer?.value}
        companyDefaultPattern={findDefaultWorkingPattern(
          watchValues?.employer?.fullData?.salaryWorkingPatterns,
        )}
        dateOfJoining={initialValues?.employmentStartDate}
      />
    </AppFormContainer>
  );
};

export default EmploymentForm;
